import {
  Avatar,
  Chip,
  Link,
  LinkProps,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";
// material-ui
import { useTheme } from "@mui/material/styles";
import { forwardRef, useEffect } from "react";
import { useLocation } from "react-router-dom";

import Tooltip from "@/components/@extended/Tooltip";
import { useDispatch, useSelector } from "@/main-store/hooks";
// project import
import { activeItem, openDrawer } from "@/main-store/reducers/menu";
// types
import { NavItemType } from "@/types/menu";
import { getTestId } from "@/utils/test-utils";

import NavItemButton, { NavItemButtonProps } from "./NavItemButton";

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

interface NavItemProps {
  item: NavItemType;
  level: number;
  isParents?: boolean;
  onClick?: (id: string | undefined) => void;
}

const createItemProps = (item: NavItemType, isButton: boolean): Partial<NavItemButtonProps> => {
  const itemTarget = item.target ? "_blank" : "_self";

  if (item.external) {
    return {
      component: "a",
      href: item.url,
      target: itemTarget,
    };
  }

  if (isButton) {
    return {
      component: "button",
    };
  }

  return {
    component: forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
      <Link ref={ref} {...props} href={item.url} target={itemTarget} />
    )),
  };
};

const NavItem = ({ item, level, onClick }: NavItemProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));

  const { drawerOpen, openItem } = useSelector((state) => state.menu);

  const itemHandler = (id: string | undefined) => {
    if (onClick) {
      onClick(id);
    } else if (id) {
      dispatch(activeItem({ openItem: [id] }));
    }
    if (matchDownLG) {
      dispatch(openDrawer({ drawerOpen: false }));
    }
  };

  const isSelected = openItem.findIndex((id: string) => id === item.id) > -1;

  const textColor = drawerOpen ? "#202020" : "#262626";
  const selectedTextColor = "#202020";

  const Icon = item.icon;
  const itemIcon = Icon ? (
    <Icon
      style={{
        color: isSelected ? selectedTextColor : textColor,
        fontSize: drawerOpen ? "16px" : "20px",
      }}
    />
  ) : (
    false
  );

  // active menu item on page load
  useEffect(() => {
    const isPathInExtraUrls = item.extraUrls?.some((extraUrl: string) =>
      pathname.includes(extraUrl)
    );

    if ((item.url && pathname.includes(item.url)) || isPathInExtraUrls) {
      dispatch(activeItem({ openItem: [item.id] }));
    } else if (pathname !== "/" && isSelected) {
      dispatch(activeItem({ openItem: [] }));
    }
  }, [pathname]);

  const listItemProps = createItemProps(item, !!onClick);

  return (
    <Tooltip
      title={item.title}
      placement="right"
      arrow
      disableHoverListener={drawerOpen || (!drawerOpen && level !== 1)}
    >
      <NavItemButton
        {...listItemProps}
        {...getTestId("nav-item-button")}
        item={item}
        level={level}
        drawerOpen={drawerOpen}
        onClick={() => itemHandler(item.id)}
        selected={isSelected}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              color: isSelected ? selectedTextColor : textColor,
              ...(!drawerOpen && {
                alignItems: "center",
                justifyContent: "center",
              }),
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            sx={{
              margin: 0,
            }}
            primary={
              <Typography
                variant="h6"
                sx={{
                  color: isSelected ? selectedTextColor : textColor,
                  fontWeight: isSelected ? 600 : 500,
                  my: 0,
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </NavItemButton>
    </Tooltip>
  );
};

export default NavItem;
